import { useState, type FormEvent } from 'react';
import { Link, Outlet } from 'react-router';
import { LogIn, Menu, Search, X, ArrowRight, Mail, SquarePlay } from 'lucide-react';
import { useAuthUser } from '@/features/auth/AuthContext';
import { LogoMark } from '@/components/ui/LogoMark';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';
import { canSubmitNewsletter, getNewsletterConfig } from '@/lib/newsletter';
import { LocaleProvider, useT, type MessageKey } from '@/i18n';
import { LanguageToggle } from '@/i18n/LanguageToggle';

const NAV_ITEMS: Array<{ nameKey: MessageKey; href: string }> = [
  { nameKey: 'categories.marketPeripheral', href: '/category/market-peripheral' },
  { nameKey: 'categories.analysis', href: '/category/analysis' },
  { nameKey: 'categories.insights', href: '/category/insights' },
  { nameKey: 'nav.about', href: '/about' },
];

const FOOTER_SECTIONS: Array<{ titleKey: MessageKey; links: Array<{ nameKey: MessageKey; href: string }> }> = [
  {
    titleKey: 'footer.research',
    links: [
      { nameKey: 'categories.marketPeripheral', href: '/category/market-peripheral' },
      { nameKey: 'categories.analysis', href: '/category/analysis' },
      { nameKey: 'categories.insights', href: '/category/insights' },
    ],
  },
  {
    titleKey: 'footer.firm',
    links: [
      { nameKey: 'nav.about', href: '/about' },
      { nameKey: 'footer.methodology', href: '/about' },
      { nameKey: 'footer.disclosures', href: '/about' },
      { nameKey: 'footer.orderReport', href: '/order' },
    ],
  },
];

const SOCIAL_LINKS = [
  { name: 'YouTube', icon: SquarePlay, href: 'https://www.youtube.com/@AheadMetric' },
];

function SiteHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, signOut } = useAuthUser();
  const t = useT();

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b" style={{ borderColor: '#cccccc' }}>
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className="flex h-16 items-center justify-between gap-6">
          <div className="flex min-w-0 items-center">
            <Link to="/" className="flex items-center gap-3" style={{ textDecoration: 'none' }}>
              <LogoMark className="h-8 w-8 flex-shrink-0" />
              <span className="hidden md:flex md:flex-col md:leading-none">
                <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-charcoal-500" style={{ fontFamily: "'DM Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', system-ui, sans-serif" }}>
                  {t('header.eyebrow')}
                </span>
                <span className="font-display text-xl font-bold text-[#111111]">
                  AheadMetric
                </span>
              </span>
            </Link>
          </div>

          <div className="hidden lg:flex lg:items-center lg:gap-8">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.nameKey}
                to={item.href}
                className="font-sans text-[14px] font-medium text-charcoal-700 transition-ui hover:text-[#0274b6]"
                style={{ textDecoration: 'none' }}
              >
                {t(item.nameKey)}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex md:items-center md:gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal-400" />
              <input
                type="search"
                placeholder={t('header.searchPlaceholder')}
                className="h-9 w-52 border border-charcoal-300 bg-white pl-10 font-sans text-[14px] text-[#222222] placeholder:text-charcoal-400 transition-ui focus-visible:outline-none focus-visible:border-[#0274b6] focus-visible:ring-1 focus-visible:ring-[#0274b6]"
              />
            </div>
            <LanguageToggle />
            {isAuthenticated ? (
              <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                {user?.email ?? t('header.account')}
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/login" style={{ textDecoration: 'none' }}>
                    <LogIn className="h-4 w-4" />
                    {t('header.logIn')}
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/register" style={{ textDecoration: 'none' }}>{t('header.register')}</Link>
                </Button>
              </>
            )}
            <Button asChild size="sm">
              <Link to="/order" style={{ textDecoration: 'none' }}>{t('header.subscribe')}</Link>
            </Button>
          </div>

          <div className="flex md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t('header.toggleMenu')}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="border-t bg-white lg:hidden" style={{ borderColor: '#ebebeb' }}>
          <div className="space-y-1 px-4 pb-5 pt-3">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.nameKey}
                to={item.href}
                className="block px-3 py-2.5 transition-ui"
                style={{ fontFamily: "'DM Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', system-ui, sans-serif", fontSize: '16px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.062em', color: '#333333', textDecoration: 'none' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t(item.nameKey)}
              </Link>
            ))}
            <div className="space-y-3 border-t pt-4" style={{ borderColor: '#ebebeb' }}>
              <LanguageToggle />
              {isAuthenticated ? (
                <Button variant="secondary" className="w-full" onClick={() => void signOut()}>
                  {user?.email ?? t('header.account')}
                </Button>
              ) : (
                <>
                  <Button variant="secondary" className="w-full" asChild>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: 'none' }}>
                      <LogIn className="h-4 w-4" />
                      {t('header.logIn')}
                    </Link>
                  </Button>
                  <Button variant="secondary" className="w-full" asChild>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: 'none' }}>
                      {t('header.register')}
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function FooterNewsletterSection() {
  const [email, setEmail] = useState('');
  const config = getNewsletterConfig();
  const t = useT();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    void trackEvent('newsletter_submit', { placement: 'footer_newsletter', provider: config.provider });
    if (!canSubmitNewsletter(config)) {
      e.preventDefault();
      setEmail('');
    }
  };

  return (
    <div className="mb-10 border-b pb-10" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
      <div className="grid gap-8 md:grid-cols-[1.2fr_1fr] md:items-center">
        <div>
          <p className="section-flag mb-3 border-t-2 pt-2" style={{ color: '#cccccc', borderColor: 'rgba(255,255,255,0.3)' }}>
            {t('footer.newsletterKicker')}
          </p>
          <h3 className="mb-3" style={{ fontFamily: "'Playfair Display', Georgia, 'Songti SC', 'Noto Serif CJK SC', 'SimSun', serif", fontSize: '24px', fontWeight: 700, color: '#ffffff' }}>
            {t('footer.newsletterHeading')}
          </h3>
          <p style={{ fontFamily: "'DM Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', system-ui, sans-serif", fontSize: '14px', lineHeight: '1.6', color: '#6A6A6A' }}>
            {t('footer.newsletterBody')}
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-3 sm:flex-row"
          action={config.formActionUrl || undefined}
          method={canSubmitNewsletter(config) ? 'post' : undefined}
        >
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: '#6A6A6A' }} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('footer.emailPlaceholder')}
              required
              name="email_address"
              aria-label={t('footer.emailAriaLabel')}
              className="w-full h-10 border pl-10"
              style={{ background: '#1a2c2a', borderColor: 'rgba(255,255,255,0.1)', color: '#ffffff', fontFamily: "'DM Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', system-ui, sans-serif", outline: 'none' }}
            />
          </div>
          {config.formId ? <input type="hidden" name="form_id" value={config.formId} /> : null}
          <button
            type="submit"
            className="btn-secondary min-w-36"
            style={{ color: '#ffffff', borderColor: '#ffffff', background: 'transparent' }}
          >
            {t('footer.joinLetter')}
          </button>
        </form>
      </div>
    </div>
  );
}

function SiteFooter() {
  const year = new Date().getFullYear();
  const t = useT();

  return (
    <footer style={{ background: '#223936', color: '#e0e0e0' }}>
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <FooterNewsletterSection />

        <div className="mb-10 grid gap-10 md:grid-cols-[1.4fr_repeat(2,minmax(0,1fr))]">
          <div>
            <Link to="/" className="mb-5 flex items-center gap-3" style={{ textDecoration: 'none' }}>
              <LogoMark className="h-8 w-8 flex-shrink-0" />
              <span style={{ fontFamily: "'Playfair Display', Georgia, 'Songti SC', 'Noto Serif CJK SC', 'SimSun', serif", fontSize: '18px', fontWeight: 700, color: '#ffffff' }}>
                AheadMetric
              </span>
            </Link>
            <p className="mb-5" style={{ fontFamily: "'DM Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', system-ui, sans-serif", fontSize: '14px', lineHeight: '1.6', color: '#6A6A6A', maxWidth: '28rem' }}>
              {t('footer.blurb')}
            </p>
            <div className="mb-6 flex gap-3">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="p-2 transition-ui"
                  style={{ background: '#1a2c2a', color: '#6A6A6A' }}
                  aria-label={social.name}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {FOOTER_SECTIONS.map((section) => (
            <div key={section.titleKey}>
              <h4 className="mb-4 section-flag" style={{ color: '#6A6A6A' }}>{t(section.titleKey)}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.nameKey}>
                    <Link
                      to={link.href}
                      className="inline-flex items-center gap-2 text-sm transition-ui"
                      style={{ color: '#cccccc', textDecoration: 'none' }}
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                      {t(link.nameKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t pt-8" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p style={{ fontFamily: "'DM Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', system-ui, sans-serif", fontSize: '14px', color: '#6A6A6A' }}>
              {t('footer.copyright', { year })}
            </p>
            <p style={{ fontFamily: "'DM Sans', 'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', system-ui, sans-serif", fontSize: '12px', lineHeight: '1.6', color: '#6A6A6A', maxWidth: '36rem', textAlign: 'right' }}>
              <strong style={{ color: '#cccccc' }}>{t('footer.disclaimerLabel')}</strong> {t('footer.disclaimerText')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function MainLayout() {
  return (
    <LocaleProvider>
      <div style={{ minHeight: '100vh', background: '#ffffff', display: 'flex', flexDirection: 'column' }}>
        <SiteHeader />
        <main style={{ flex: 1 }}>
          <Outlet />
        </main>
        <SiteFooter />
      </div>
    </LocaleProvider>
  );
}
